import { useTranslation } from "@i18n";
import React, { useState } from "react";
import IconComponent from "@CubeComponents/icons";
import useModulesMapper from "@Components/StickySidebarViewer/ModulesMapper";

import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * One accordion group in either DnD pane: a single-key object mapping the group name (the
 * `belongToGroup` of ModulesMapper) to the sidebar module ids it holds. Built by
 * DesignViewerLayoutManager's `makeDnDComponentData`.
 */
export type SidebarGroup = Record<string, { widgets: string[] }>;

/** The group + items a drag started from; `index` is the group's position in its pane. */
type DragSelection = { accordionName: string; itemArray: string[]; index: number };

/** `droppable` is a legacy non-standard attribute React does not declare on intrinsic elements. */
type DroppableAttr = { droppable: string };

interface DesignViewerLayoutSettingComponentProps {
  availableData: SidebarGroup[];
  setAvailableData: (data: SidebarGroup[]) => void;
  addedData: SidebarGroup[];
  setAddedData: (data: SidebarGroup[]) => void;
}

function DesignViewerLayoutSettingComponent(props: DesignViewerLayoutSettingComponentProps) {
  const { t } = useTranslation();
  const [selectedData, setSelectedData] = useState<DragSelection | null>(null);
  const [onPositionAccordion, setOnPositionAccordion] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  // KNOWN BUG, preserved verbatim: `onClickAccordion` pushes the DOM `data-index` attribute, i.e. a
  // STRING, while `availableContent` below tests `.includes(index)` with the NUMERIC index, so the
  // highlight branch can never match. Typed to the value actually stored.
  const [selectedAccordion, setSelectedAccordion] = useState<string[]>([]);
  const [modulesMapper] = useModulesMapper(props);

  function onDragOver1(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
  }
  function onDragOver2(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
  }
  function availableContent(itemArray: string[], accordionName: string, index: number) {
    return itemArray
      .filter((data) => modulesMapper[data])
      .map((data: string) => (
        <div
          key={index}
          className={
            // See the `selectedAccordion` declaration: string entries vs a numeric `index`. The
            // array is widened here rather than converting either side, which would change behaviour.
            (selectedAccordion as (string | number)[]).includes(index)
              ? "flex flex-row items-center gap-3.5 py-4 ps-6 text-sm"
              : "flex flex-row items-center gap-3.5 py-4 ps-6 text-sm bg-[var(--surface-white)]"
          }
          draggable
          {...({ droppable: "true" } as DroppableAttr)}
          onDragStart={(e) =>
            dragStartInsideAvailable(e, {
              accordionName,
              itemArray: [data],
              index,
            })
          }
        >
          <IconComponent name={`SidebarIcon/${modulesMapper[data].name}`} height="1.25rem" width="1.25rem" />
          <div>{modulesMapper[data].name}</div>
        </div>
      ));
  }

  function addedContent(itemArray: string[], accordionName: string, index: number) {
    return itemArray
      .filter((data) => modulesMapper[data])
      .map((data: string) => (
        <div
          key={index}
          className="flex flex-row items-center gap-3.5 py-4 ps-6 text-sm bg-[var(--surface-white)]"
          draggable
          onDragStart={(e) => dragStartInsideAdded(e, { accordionName, itemArray: [data], index })}
        >
          <IconComponent name={`SidebarIcon/${modulesMapper[data].name}`} height="1.25rem" width="1.25rem" />
          <div>{modulesMapper[data].name}</div>
        </div>
      ));
  }
  function headerAvailable(accordionName: string) {
    return <div className="text-sm font-semibold py-1">{accordionName}</div>;
  }
  function headerAdded(accordionName: string) {
    return <div className="text-sm font-semibold py-1">{accordionName}</div>;
  }

  // -------------------------------dragStart---------------------------

  function dragStartAvailable(e: React.DragEvent<HTMLElement>, itemsData: DragSelection) {
    setSelectedData(itemsData);
    setDragging(1);
  }
  function dragStartAdded(e: React.DragEvent<HTMLElement>, itemsData: DragSelection) {
    setSelectedData(itemsData);
    setDragging(2);
  }

  // -------------------------------dragStartInside-----------------------

  function dragStartInsideAvailable(e: React.DragEvent<HTMLElement>, itemsData: DragSelection) {
    setSelectedData(itemsData);
    setDragging(1);
    e.stopPropagation();
  }
  function dragStartInsideAdded(e: React.DragEvent<HTMLElement>, itemsData: DragSelection) {
    setSelectedData(itemsData);
    setDragging(2);
    e.stopPropagation();
  }

  // ---------------------------------onDrop-----------------------------
  function onDropAvailable() {
    if (dragging == 1) {
      if (onPositionAccordion == null) {
        return;
      }
      // `selectedData` is only non-null once a drag has started, and the pane always holds the group
      // the drag came from; both are dereferenced unguarded here, so the assertions are erased only.
      if (
        selectedData!.itemArray.length !=
        props.availableData[selectedData!.index]![selectedData!.accordionName]!.widgets.length
      ) {
        return;
      }
      const dataAvailable = selfArrangeAccordion(props.availableData);
      props.setAvailableData(dataAvailable);
      return;
    }
    if (selectedData == null) {
      return;
    }
    // ADD
    const selectedDataItemArray = [...selectedData.itemArray];
    const dataAvailable = addToOtherSide(props.availableData, selectedDataItemArray);
    props.setAvailableData(dataAvailable);
    // REMOVE
    const dataAdded = removeFromCurrentSide(props.addedData, selectedDataItemArray);
    props.setAddedData(dataAdded);
  }

  function onDropAdded() {
    if (dragging == 2) {
      if (onPositionAccordion == null) {
        return;
      }
      if (
        selectedData!.itemArray.length !=
        props.addedData[selectedData!.index]![selectedData!.accordionName]!.widgets.length
      ) {
        return;
      }
      const dataAdded = selfArrangeAccordion(props.addedData);
      props.setAddedData(dataAdded);
      return;
    }
    if (selectedData == null) {
      return;
    }
    // ADD
    const selectedDataItemArray = [...selectedData.itemArray];
    const dataAdded = addToOtherSide(props.addedData, selectedDataItemArray);
    props.setAddedData(dataAdded);
    // REMOVE
    const dataAvailable = removeFromCurrentSide(props.availableData, selectedDataItemArray);
    props.setAvailableData(dataAvailable);
  }

  // The three helpers below read `selectedData` / `onPositionAccordion` through the closure, where
  // the callers' null checks do not narrow, and index into the panes unguarded. All assertions here
  // are erased; guards would change behaviour.
  function selfArrangeAccordion(items: SidebarGroup[]) {
    const data = [...items];
    const fromIndex = selectedData!.index;
    const toIndex = onPositionAccordion;
    const element = data.splice(fromIndex, 1)[0];
    data.splice(toIndex!, 0, element!);
    setOnPositionAccordion(null);
    return data;
  }

  function addToOtherSide(item: SidebarGroup[], selectedDataItemArray: string[]) {
    const data = [...item];
    // Matching groups are collected first: an assignment made inside a `map` callback is invisible
    // to control-flow analysis, so the "was it already there" answer comes from the match list.
    const matched = data.filter((object: SidebarGroup) => Object.keys(object)[0] == selectedData!.accordionName);
    matched.forEach((object: SidebarGroup) => {
      object[selectedData!.accordionName]!.widgets.push(...selectedDataItemArray);
    });
    if (matched.length === 0) {
      if (onPositionAccordion != null) {
        data.splice(onPositionAccordion, 0, {
          [selectedData!.accordionName]: { widgets: [...selectedDataItemArray] },
        });
        setOnPositionAccordion(null);
      } else {
        data.push({
          [selectedData!.accordionName]: { widgets: [...selectedDataItemArray] },
        });
      }
    }
    return data;
  }

  function removeFromCurrentSide(item: SidebarGroup[], selectedDataItemArray: string[]) {
    const data = [...item];
    selectedDataItemArray.map((item: string) => {
      const indexOfItem = data[selectedData!.index]![selectedData!.accordionName]!.widgets.indexOf(item);
      data[selectedData!.index]![selectedData!.accordionName]!.widgets.splice(indexOfItem, 1);
    });
    if (data[selectedData!.index]![selectedData!.accordionName]!.widgets.length == 0) {
      data.splice(selectedData!.index, 1);
    }
    return data;
  }

  // ---------------------onDragEnter--------------------------------------

  function onDragEnterAvailable(index: number) {
    setOnPositionAccordion(index);
  }
  function onDragEnterAdded(index: number) {
    setOnPositionAccordion(index);
  }

  // -----------------------onDragEnd---------------------------------------
  function onDragEndAvailable() {
    setSelectedData(null);
    setOnPositionAccordion(null);
    setDragging(null);
  }
  function onDragEndAdded() {
    setSelectedData(null);
    setOnPositionAccordion(null);
    setDragging(null);
  }
  // /////////////////////////////////////
  // function onDragLeave() {
  //   console.log('leave');
  // }
  // /////////////////////////////////////
  function onClickAccordion(e: React.MouseEvent<HTMLElement>) {
    // `data-index` is set on every accordion wrapper below; `getAttribute` is the typed way to read
    // it, and the click is ignored rather than throwing if the attribute is ever missing.
    const index = e.currentTarget.getAttribute("data-index");
    if (index === null) return;
    const selectedaccordion = selectedAccordion;
    selectedaccordion.push(index);
    setSelectedAccordion(selectedaccordion);
  }

  return (
    <div className="flex justify-between px-10 gap-[4%] text-[var(--primary-text)] text-sm font-semibold w-full overflow-hidden pb-5 h-full">
      <div className="w-full h-full pt-7 flex flex-col gap-7">
        <div>{t("Available")}</div>
        <div
          className="border-px border-[var(--surface-stroke-gradient-color1)] shadow-[0.25rem_0.25rem_0.9375rem_rgb(65_92_171_/_15%)] rounded-lg overflow-auto h-full px-4"
          {...({ droppable: "true" } as DroppableAttr)}
          onDragOver={onDragOver1}
          onDrop={() => onDropAvailable()}
          onDragEnd={onDragEndAvailable}
        >
          {props.availableData.map((object: SidebarGroup, index: number) => {
            // A group object holds exactly one entry: the accordion name mapped to its widget list.
            // Taking the entry keeps the two together, so neither has to be looked up again.
            const groupEntry = Object.entries(object)[0];
            const accordionName = groupEntry ? groupEntry[0] : "";
            const group = groupEntry ? groupEntry[1] : { widgets: [] };
            return (
              <div
                key={index}
                data-index={index}
                onClick={onClickAccordion}
                draggable
                onDragStart={(e) =>
                  dragStartAvailable(e, {
                    accordionName,
                    itemArray: group.widgets,
                    index,
                  })
                }
                onDragEnter={() => onDragEnterAvailable(index)}
              >
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>{headerAvailable(accordionName)}</AccordionSummary>
                  <AccordionDetails>{availableContent(group.widgets, accordionName, index)}</AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        </div>
      </div>
      {/* <div className={css.availableOptionContainer}>
        <div className={css.optionStyle}>
          <IconComponent
            name="ArrowsUp"
            height="16px"
            width="16px"
          />
        </div>
        <div className={css.optionStyle}>
          <IconComponent
            name="arrowDown"
            height="16px"
            width="16px"
          />
        </div>
        <div className={css.optionStyle}>
          <IconComponent
            name="BackwardArrow"
            height="16px"
            width="16px"
          />
        </div>
        <div className={css.optionStyle}>
          <IconComponent
            name="ForwardArrow"
            height="16px"
            width="16px"
          />
        </div>
        <div className={css.optionStyle}>
          <IconComponent
            name="BackwardArrow"
            height="16px"
            width="16px"
          />
          <IconComponent
            name="BackwardArrow"
            height="16px"
            width="16px"
          />
        </div>
        <div className={css.optionStyle}>
          <IconComponent
            name="ForwardArrow"
            height="16px"
            width="16px"
          />
          <IconComponent
            name="ForwardArrow"
            height="16px"
            width="16px"
          />
        </div>
      </div> */}
      <div className="w-full h-full pt-7 flex flex-col gap-7">
        <div>{t("Added")}</div>
        <div
          className="border-px border-[var(--surface-stroke-gradient-color1)] shadow-[0.25rem_0.25rem_0.9375rem_rgb(65_92_171_/_15%)] rounded-lg overflow-auto h-full px-4"
          {...({ droppable: "true" } as DroppableAttr)}
          onDragOver={onDragOver2}
          onDrop={() => onDropAdded()}
          onDragEnd={onDragEndAdded}
        >
          {props.addedData.map((object: SidebarGroup, index: number) => {
            const groupEntry = Object.entries(object)[0];
            const accordionName = groupEntry ? groupEntry[0] : "";
            const group = groupEntry ? groupEntry[1] : { widgets: [] };
            return (
              <div
                key={index}
                draggable
                onDragStart={(e) =>
                  dragStartAdded(e, {
                    accordionName,
                    itemArray: group.widgets,
                    index,
                  })
                }
                onDragEnter={() => onDragEnterAdded(index)}
              >
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>{headerAdded(accordionName)}</AccordionSummary>
                  <AccordionDetails style={{ height: "100%" }}>
                    {addedContent(group.widgets, accordionName, index)}
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DesignViewerLayoutSettingComponent;
